import { SEARCH_MAX_LENGTH } from "./ResourceSearch";

/**
 * Where the Resources list keeps its filters so Back from a resource restores
 * them. The category is in the URL (`?category=`), so it also survives a
 * refresh. The search text is only in the history entry's state: on a shared
 * kiosk or iPad, terms like "detox" never reach the address bar or the
 * browser's history list. The browser keeps that state with the entry, so a
 * refresh or Back in the same tab restores the search.
 */
export type ListHistoryState = {
  q?: string;
  /** The resource that was opened, focused again on return. */
  returnFocusId?: string;
};

/** What the detail page gets, so "All resources" can return to the list. */
export type DetailHistoryState = {
  fromList: true;
  /** The list's `?category=` query, or "". */
  listSearch: string;
};

export type ListState = {
  categoryId: string | null;
  query: string;
  returnFocusId?: string;
};

const CATEGORY_PARAM = "category";

const stringField = (state: unknown, key: string): string | undefined => {
  const value =
    state && typeof state === "object"
      ? (state as Record<string, unknown>)[key]
      : undefined;
  return typeof value === "string" ? value : undefined;
};

export const readListState = (search: string, state: unknown): ListState => {
  const categoryId = new URLSearchParams(search).get(CATEGORY_PARAM) || null;
  return {
    categoryId,
    query: (stringField(state, "q") ?? "").slice(0, SEARCH_MAX_LENGTH),
    returnFocusId: stringField(state, "returnFocusId") || undefined,
  };
};

/** `?category=<id>`, or "" for all categories. */
export const listSearch = (categoryId: string | null): string =>
  categoryId
    ? `?${new URLSearchParams({ [CATEGORY_PARAM]: categoryId }).toString()}`
    : "";

/** History state for the list entry; empty values are left out. */
export const listHistoryState = (
  query: string,
  returnFocusId?: string,
): ListHistoryState => ({
  ...(query ? { q: query } : {}),
  ...(returnFocusId ? { returnFocusId } : {}),
});

export const readDetailState = (
  state: unknown,
): DetailHistoryState | undefined => {
  if (
    !state ||
    typeof state !== "object" ||
    (state as Record<string, unknown>).fromList !== true
  ) {
    return undefined;
  }
  const raw = stringField(state, "listSearch") ?? "";
  const categoryId = readListState(raw, undefined).categoryId;
  // Rebuilt rather than trusted, so only the category can ride along.
  return { fromList: true, listSearch: listSearch(categoryId) };
};
